import type { LeapBotConversation } from '../../core/interfaces/leapbot.interface';

/** A titled group of conversations in the history list. */
export interface LeapBotConversationGroup {
  /** `Hoy`, `Ayer`, `Esta semana` or `Antes`. */
  label: string;
  conversations: LeapBotConversation[];
}

const GROUP_ORDER = ['Hoy', 'Ayer', 'Esta semana', 'Antes'] as const;
const DAY_MS = 24 * 60 * 60 * 1000;

function startOfDay(date: Date): number {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime();
}

function normalize(text: string): string {
  return text.normalize('NFD').replace(/\p{M}/gu, '').toLowerCase();
}

function groupLabel(updatedAt: string, now: Date): (typeof GROUP_ORDER)[number] {
  const daysAgo = Math.round((startOfDay(now) - startOfDay(new Date(updatedAt))) / DAY_MS);
  if (daysAgo <= 0) return 'Hoy';
  if (daysAgo === 1) return 'Ayer';
  if (daysAgo < 7) return 'Esta semana';
  return 'Antes';
}

/**
 * Filters the conversations by a search text (title or message content, ignoring case and
 * accents) and groups them as `Hoy`, `Ayer`, `Esta semana` and `Antes`, hiding empty groups. The
 * order of the input is kept inside each group.
 *
 * @param conversations - Conversations, most recent first.
 * @param query - Text typed in the search field; blank matches everything.
 * @param now - Reference date, injected so the result is testable.
 * @example
 * groupConversations(list, 'logístico', new Date())[0].label; // 'Hoy'
 */
export function groupConversations(
  conversations: readonly LeapBotConversation[],
  query: string,
  now: Date,
): LeapBotConversationGroup[] {
  const needle = normalize(query.trim());
  const matches = (c: LeapBotConversation): boolean =>
    needle.length === 0 ||
    normalize(c.title).includes(needle) ||
    c.messages.some((m) => normalize(m.content).includes(needle));

  const buckets = new Map<string, LeapBotConversation[]>(GROUP_ORDER.map((label) => [label, []]));
  for (const conversation of conversations) {
    if (matches(conversation)) buckets.get(groupLabel(conversation.updatedAt, now))?.push(conversation);
  }
  return GROUP_ORDER.map((label) => ({ label, conversations: buckets.get(label) ?? [] })).filter(
    (group) => group.conversations.length > 0,
  );
}
